import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './client';

export type DraftKind = 'quote' | 'item' | 'customer' | 'proposal';

export interface FormDraft {
  kind: DraftKind;
  payload: unknown;
  updatedAt: string;
}

/** All of the current user's in-progress drafts (for showing "Draft" rows in lists). */
export function useDrafts() {
  return useQuery({ queryKey: ['drafts'], queryFn: () => api.get<FormDraft[]>('/api/drafts'), staleTime: 3_000 });
}

/**
 * Server-side autosave for a form. On mount it loads the user's single draft of this
 * kind (hydrating the form once — skipped if the user already started typing), then
 * debounce-saves whenever `value` changes. A failed load disables autosave (so a
 * transient error can't arm an overwrite of the stored draft).
 */
export function useFormDraft<T>(kind: DraftKind, value: T, onHydrate: (payload: T) => void, enabled = true) {
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const baseline = useRef<string | null>(null); // JSON of the last state we consider persisted
  const hydrateRef = useRef(onHydrate);
  hydrateRef.current = onHydrate;

  const json = JSON.stringify(value);
  const jsonRef = useRef(json);
  jsonRef.current = json;
  const initialJson = useRef(json); // form state at mount — hydration only applies while untouched

  // Load once.
  useEffect(() => {
    if (!enabled) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    api
      .get<FormDraft | null>(`/api/drafts/${kind}`)
      .then((d) => {
        if (cancelled) return;
        // Only hydrate if the user hasn't typed anything while the draft was loading.
        if (d?.payload != null && jsonRef.current === initialJson.current) hydrateRef.current(d.payload as T);
        baseline.current = null; // set on the first post-load effect run below
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true); // autosave stays disarmed — do not overwrite the stored draft
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, enabled]);

  useEffect(() => {
    if (!loaded || !enabled || loadFailed) return;
    if (baseline.current === null) {
      baseline.current = json; // baseline = whatever state we have right after load (empty or hydrated)
      return;
    }
    if (json === baseline.current) return;
    const t = setTimeout(() => {
      const saved = json;
      api
        .put(`/api/drafts/${kind}`, { payload: JSON.parse(saved) })
        .then(() => {
          baseline.current = saved; // only advance the baseline when the save actually landed
          setSavedAt(Date.now());
        })
        .catch(() => {});
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [json, loaded, enabled, loadFailed]);

  /** Delete the server draft. Pass the form state you are about to reset to (if any)
      so the autosave baseline matches it and doesn't immediately re-create the draft. */
  const discard = (next?: T) => {
    baseline.current = next !== undefined ? JSON.stringify(next) : json;
    return api.del(`/api/drafts/${kind}`).catch(() => {});
  };

  return { loaded, loadFailed, discard, savedAt };
}
